import { useState } from 'react';
import './BShareModal.css';

interface BShareModalProps {
  title: string;
  url: string;
  onClose: () => void;
}

interface ShareTarget {
  name: string;
  icon: string;
  getUrl: (url: string, title: string) => string;
}

const SHARE_TARGETS: ShareTarget[] = [
  {
    name: 'Twitter',
    icon: '𝕏',
    getUrl: (url, title) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  },
  {
    name: 'Facebook',
    icon: 'f',
    getUrl: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    name: 'Reddit',
    icon: '●',
    getUrl: (url, title) =>
      `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  },
  {
    name: 'WhatsApp',
    icon: '◯',
    getUrl: (url, title) =>
      `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
  },
  {
    name: 'Telegram',
    icon: '➤',
    getUrl: (url, title) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    name: 'Email',
    icon: '✉',
    getUrl: (url, title) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent('Check this out: ' + url)}`,
  },
  {
    name: 'LinkedIn',
    icon: 'in',
    getUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    name: 'iMessage',
    icon: '◉',
    getUrl: (url, title) =>
      `sms:&body=${encodeURIComponent(title + ' ' + url)}`,
  },
];

/**
 * Share modal with copy link and social sharing options.
 */
export function BShareModal({ title, url, onClose }: BShareModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="b-share-overlay" onClick={handleBackdropClick}>
      <div className="b-share-modal">
        <button className="b-share-modal__close" onClick={onClose}>
          ×
        </button>

        <h3 className="b-share-modal__title">Share This Report</h3>

        <button
          className={`b-share-modal__copy ${copied ? 'b-share-modal__copy--copied' : ''}`}
          onClick={handleCopyLink}
        >
          {copied ? '✓ Copied!' : 'Copy Link'}
        </button>

        <p className="b-share-modal__divider">or share to</p>

        <div className="b-share-modal__targets">
          {SHARE_TARGETS.map((target) => (
            <a
              key={target.name}
              href={target.getUrl(url, title)}
              target="_blank"
              rel="noopener noreferrer"
              className="b-share-target"
            >
              <span className="b-share-target__icon">{target.icon}</span>
              <span className="b-share-target__name">{target.name}</span>
            </a>
          ))}
        </div>

      </div>
    </div>
  );
}
